import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import UIContainer from 'components/ui/UIContainer';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import FilledActionButton from 'components/FilledActionButton';
import { NotificationCenterContext } from 'components/NotificationCenter';
import PageIntro from 'components/PageIntro/PageIntro';
import { usePageIntro } from 'components/PageIntro/PageIntroContext';
import WithLoader from 'components/WithLoader';
import CompactPagination from 'components/CompactPagination';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { commonActions } from 'app/store/common.slice';
import { DictionaryWord, LearningSession, VocabularyWordType } from 'app/types';
import { createDefaultColumnSettings } from 'components/tableSettings';
import { useTableSettings } from 'hooks/useTableSettings';
import { axiosInstance } from 'setup/axiosInstance';
import { fillUrlIds, pagePaths } from 'routes/paths';
import { useNavigate, useParams } from 'react-router-dom';
import VocabularyWordsView from 'components/vocabularyWordsView/VocabularyWordsView';
import VocabularyStatsGrid from 'components/vocabularyWordsView/VocabularyStatsGrid';
import { useVocabularyWordsView } from 'components/vocabularyWordsView/useVocabularyWordsView';
import { WordStatus, fmtN } from 'components/vocabularyWordsView/meta';
import { ColumnFilters } from 'components/WordsTable';

interface IVocabularyWordsPageProps {
  wordType: VocabularyWordType;
}

const DEFAULT_VOCABULARY_WORD_COLUMNS = createDefaultColumnSettings({
  number: true,
  word: true,
  transcription: true,
  translation: true,
  partOfSpeech: true,
  definition: true,
  // Single status column replaces the legacy isLearned / isActiveSpeech pair —
  // mirrors the BE-side enum the API will eventually return.
  status: true,
  addedAt: true,
});

const VocabularyWordsPage: React.FC<IVocabularyWordsPageProps> = ({
  wordType,
}) => {
  const navigate = useNavigate();
  const { vocabularyPage } = useParams();
  const dispatch = useAppDispatch();
  const { pushNotification } = useContext(NotificationCenterContext);
  // Read PageIntro state — when the user collapses the hero, we still
  // want a breath of space between the breadcrumbs/CTA row and the
  // filter bar below so the two control rows don't visually fuse.
  const { collapsed: introCollapsed } = usePageIntro();
  const { data, loading, partOfSpeechOptions, totalElements } = useAppSelector(
    (store) =>
      wordType === 'EXPRESSION'
        ? store.common.vocabularyExpressions
        : store.common.vocabularyWords,
  );
  const typedData = useMemo(
    () => data.filter((word) => word.wordType === wordType),
    [data, wordType],
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
  const [learningStartLoading, setLearningStartLoading] = useState(false);
  const [undoMessage, setUndoMessage] = useState<string | null>(null);
  const pendingUndoRef = useRef<DictionaryWord[] | null>(null);

  // Status/POS filters that travel to the BE — we still funnel the v2
  // chip+select UI through `columnFilters` so the existing saga doesn't change.
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({
    isActiveSpeech: 'all',
    isLearned: 'all',
    partOfSpeech: 'all',
  });

  const tableSettingsKey =
    wordType === 'EXPRESSION' ? 'VOCABULARY_EXPRESSIONS' : 'VOCABULARY_WORDS';
  const {
    resetSettings: resetTableSettings,
    saveSettings: saveTableSettings,
    settings: tableSettings,
  } = useTableSettings(tableSettingsKey, DEFAULT_VOCABULARY_WORD_COLUMNS);
  const vocabularyWordsPageSize = tableSettings.pageSize;
  const previousPageSizeRef = useRef(vocabularyWordsPageSize);

  const currentPage = useMemo(() => {
    const page = Number(vocabularyPage);
    return Number.isInteger(page) && page > 0 ? page : 1;
  }, [vocabularyPage]);

  const totalPages = Math.max(
    1,
    Math.ceil(totalElements / vocabularyWordsPageSize),
  );

  const pagePath =
    wordType === 'EXPRESSION'
      ? pagePaths.vocabularyExpressionsPage
      : pagePaths.vocabularyWordsPage;

  const navigateToPage = useCallback(
    (page: number, replace = false) => {
      navigate(
        fillUrlIds(pagePath, { vocabularyPage: String(page) }),
        { replace },
      );
    },
    [navigate, pagePath],
  );

  // ── view state hook (drives the v2 UI) ────────────────────────────────────
  const view = useVocabularyWordsView({
    words: typedData,
    onPatchWord: (id, fields) => {
      const word = typedData.find((w) => w.id === id);
      if (!word) return;
      dispatch(
        commonActions.vocabularyWordsUpdate({
          filters: columnFilters,
          page: currentPage - 1,
          size: vocabularyWordsPageSize,
          wordType,
          words: [
            {
              ...word,
              translation: fields.translation ?? word.translation,
              definition: fields.definition ?? word.definition,
            } as DictionaryWord,
          ],
        }),
      );
    },
    resetKey: currentPage,
  });

  // ── translate v2 4-state status chip → BE columnFilters (legacy booleans).
  // The BE GET endpoint still takes isLearned/isActiveSpeech; LEARNED and
  // REPEAT both map to {isLearned: true, isActiveSpeech: false} — the FE then
  // narrows down to the exact status from the loaded page. Replace this with
  // a `status` param once the BE adds it.
  useEffect(() => {
    const nextFilters: ColumnFilters = (() => {
      switch (view.statusFilter) {
        case 'ACTIVE':
          return {
            isLearned: 'all',
            isActiveSpeech: 'true',
            partOfSpeech: columnFilters.partOfSpeech,
          };
        case 'LEARNED':
        case 'REPEAT':
          return {
            isLearned: 'true',
            isActiveSpeech: 'false',
            partOfSpeech: columnFilters.partOfSpeech,
          };
        case 'NEW':
          return {
            isLearned: 'false',
            isActiveSpeech: 'false',
            partOfSpeech: columnFilters.partOfSpeech,
          };
        case 'all':
        default:
          return {
            isLearned: 'all',
            isActiveSpeech: 'all',
            partOfSpeech: columnFilters.partOfSpeech,
          };
      }
    })();
    if (
      nextFilters.isLearned !== columnFilters.isLearned ||
      nextFilters.isActiveSpeech !== columnFilters.isActiveSpeech
    ) {
      navigateToPage(1, true);
      setColumnFilters(nextFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.statusFilter]);

  // ── translate v2 POS select → BE columnFilters ───────────────────────────
  useEffect(() => {
    if (
      (view.posFilter ?? 'all') !== (columnFilters.partOfSpeech ?? 'all')
    ) {
      navigateToPage(1, true);
      setColumnFilters((prev) => ({ ...prev, partOfSpeech: view.posFilter }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.posFilter]);

  // groupBy='recent' needs addedAt-DESC paging on the BE — otherwise the
  // FE only sees the alphabetical page-1 slice and the "Нещодавні" group
  // misses every batch that doesn't happen to land in it.
  const sortBy = view.groupBy === 'recent' ? 'recent' : undefined;

  // Debounced mirror of view.search — sent BE-side so search hits the whole
  // vocabulary instead of just the current alphabetical page. 300ms keeps
  // typing snappy without firing a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const next = (view.search ?? '').trim();
    if (next === debouncedSearch) return;
    const t = setTimeout(() => {
      setDebouncedSearch(next);
      navigateToPage(1, true);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.search]);

  useEffect(() => {
    dispatch(
      commonActions.vocabularyWordsGet({
        filters: columnFilters,
        page: currentPage - 1,
        size: vocabularyWordsPageSize,
        wordType,
        sortBy,
        search: debouncedSearch || undefined,
      }),
    );
  }, [
    columnFilters,
    currentPage,
    dispatch,
    vocabularyWordsPageSize,
    wordType,
    sortBy,
    debouncedSearch,
  ]);

  useEffect(() => {
    if (vocabularyPage !== String(currentPage)) {
      navigateToPage(currentPage, true);
    }
  }, [currentPage, navigateToPage, vocabularyPage]);

  useEffect(() => {
    if (!loading && currentPage > totalPages) {
      navigateToPage(totalPages, true);
    }
  }, [currentPage, loading, navigateToPage, totalPages]);

  useEffect(() => {
    if (
      previousPageSizeRef.current !== vocabularyWordsPageSize &&
      currentPage !== 1
    ) {
      navigateToPage(1, true);
    }
    previousPageSizeRef.current = vocabularyWordsPageSize;
  }, [currentPage, navigateToPage, vocabularyWordsPageSize]);

  // ── derived aggregates for the overview header ───────────────────────────
  const aggregates = useMemo(() => {
    let learned = 0;
    let active = 0;
    const pos: Record<string, number> = {};
    typedData.forEach((w) => {
      if (w.isLearned) learned += 1;
      if (w.isActiveSpeech) active += 1;
      const key = (w.partOfSpeech ?? 'OTHER').toUpperCase();
      pos[key] = (pos[key] ?? 0) + 1;
    });
    const breakdown = Object.entries(pos)
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count);
    // For the "Усього слів" card prefer the BE-side total; learned/active on
    // the current page only — until the BE exposes aggregates we'd extrapolate
    // wrongly otherwise.
    return { learned, active, breakdown };
  }, [typedData]);

  // ── single-row mutations ─────────────────────────────────────────────────
  const dispatchSingleUpdate = useCallback(
    (word: DictionaryWord, patch: Partial<DictionaryWord>) => {
      dispatch(
        commonActions.vocabularyWordsUpdate({
          filters: columnFilters,
          page: currentPage - 1,
          size: vocabularyWordsPageSize,
          wordType,
          words: [{ ...word, ...patch } as DictionaryWord],
        }),
      );
    },
    [columnFilters, currentPage, dispatch, vocabularyWordsPageSize, wordType],
  );

  const handleSetStatus = useCallback(
    (id: number, status: WordStatus) => {
      const word = typedData.find((w) => w.id === id);
      if (!word) return;
      // BE accepts `status` and reconciles the legacy booleans on persist —
      // we still set them on the optimistic patch so the row paints right
      // away with the same render rule (`statusOf`).
      const isLearned = status === 'LEARNED' || status === 'REPEAT' || status === 'ACTIVE';
      const isActiveSpeech = status === 'ACTIVE';
      dispatchSingleUpdate(word, { status, isLearned, isActiveSpeech });
    },
    [dispatchSingleUpdate, typedData],
  );

  // ── delete (single + bulk) with undo affordance ──────────────────────────
  const finalizeDelete = useCallback(
    (ids: number[]) => {
      if (!ids.length) return;
      const nextTotalElements = Math.max(0, totalElements - ids.length);
      const nextTotalPages = Math.max(
        1,
        Math.ceil(nextTotalElements / vocabularyWordsPageSize),
      );
      const nextCurrentPage = Math.min(currentPage, nextTotalPages);
      if (nextCurrentPage !== currentPage) {
        navigateToPage(nextCurrentPage, true);
      }
      dispatch(
        commonActions.vocabularyWordsDelete({
          filters: columnFilters,
          page: nextCurrentPage - 1,
          size: vocabularyWordsPageSize,
          wordType,
          wordIds: ids,
        }),
      );
    },
    [
      columnFilters,
      currentPage,
      dispatch,
      navigateToPage,
      totalElements,
      vocabularyWordsPageSize,
      wordType,
    ],
  );

  const handleDeleteOne = useCallback(
    (id: number) => {
      setPendingDeleteIds([id]);
      setDeleteDialogOpen(true);
    },
    [],
  );

  const handleBulkDelete = useCallback(() => {
    if (!view.selectedIds.length) return;
    setPendingDeleteIds(view.selectedIds);
    setDeleteDialogOpen(true);
  }, [view.selectedIds]);

  const handleBulkSetStatus = useCallback(
    (status: WordStatus) => {
      if (!view.selectedIds.length) return;
      const isLearned =
        status === 'LEARNED' || status === 'REPEAT' || status === 'ACTIVE';
      const isActiveSpeech = status === 'ACTIVE';
      const patched = typedData
        .filter((w) => view.selectedIds.includes(w.id))
        .map(
          (w) =>
            ({ ...w, status, isLearned, isActiveSpeech } as DictionaryWord),
        );
      dispatch(
        commonActions.vocabularyWordsUpdate({
          filters: columnFilters,
          page: currentPage - 1,
          size: vocabularyWordsPageSize,
          wordType,
          words: patched,
        }),
      );
      view.clear();
    },
    [
      columnFilters,
      currentPage,
      dispatch,
      typedData,
      view,
      vocabularyWordsPageSize,
      wordType,
    ],
  );

  const confirmDelete = () => {
    const removed = typedData.filter((w) => pendingDeleteIds.includes(w.id));
    pendingUndoRef.current = removed;
    finalizeDelete(pendingDeleteIds);
    setUndoMessage(
      pendingDeleteIds.length === 1
        ? `«${removed[0]?.word ?? 'слово'}» видалено`
        : `${fmtN(pendingDeleteIds.length)} ${view.sliv(pendingDeleteIds.length)} видалено`,
    );
    setDeleteDialogOpen(false);
    setPendingDeleteIds([]);
    view.clear();
  };

  const handleStartLearning = async () => {
    if (!view.selectedIds.length) return;
    setLearningStartLoading(true);
    try {
      const { data: learningSession } = await axiosInstance.post<LearningSession>(
        '/learning-sessions',
        {
          mode: 'MANUAL',
          source: 'VOCABULARY',
          wordIds: view.selectedIds,
        },
      );
      navigate(
        fillUrlIds(pagePaths.learningSession, {
          learningSessionId: String(learningSession.id),
        }),
      );
    } finally {
      setLearningStartLoading(false);
    }
  };

  const handleUndo = () => {
    // Surface a friendly note that the BE doesn't keep tombstones — the user
    // still gets the affordance + we can wire restore-on-delete in one place
    // when the API lands.
    setUndoMessage(null);
    pendingUndoRef.current = null;
  };

  const paginationFooter =
    totalPages > 1 ? (
      <>
        <CompactPagination
          currentPage={currentPage}
          disabled={loading}
          onChange={navigateToPage}
          totalPages={totalPages}
        />
        <Typography sx={{ color: 'text.secondary', fontSize: 13.5 }}>
          {`${(currentPage - 1) * vocabularyWordsPageSize + 1}–${Math.min(
            currentPage * vocabularyWordsPageSize,
            totalElements,
          )} з ${fmtN(totalElements)} слів`}
        </Typography>
      </>
    ) : null;

  const pageTitle =
    wordType === 'EXPRESSION'
      ? 'Мій словник виразів'
      : 'Мій словник слів';

  const pageDescription =
    'Усі слова, які ти зберіг — керуй статусами, редагуй і повторюй.';

  // Don't disable the CTA when there's no selection — the gray block was
  // visually loud against the white hero. Instead, route an empty click
  // to a warning notification so the user always sees the same active
  // button and gets a hint of what to do.
  const onStartLearningClick = () => {
    if (!view.selectedIds.length) {
      pushNotification(
        'Спочатку оберіть слова, щоб почати вправи',
        'warning',
      );
      return;
    }
    handleStartLearning();
  };

  const pageActions = (
    <>
      <FilledActionButton
        startIcon={<AutoStoriesOutlinedIcon sx={{ fontSize: 17 }} />}
        onClick={onStartLearningClick}
        disabled={learningStartLoading}
      >
        {'Почати вправи'}
      </FilledActionButton>
      <FilledActionButton
        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
        onClick={() => view.setAddOpen(!view.addOpen)}
      >
        {'Додати слово'}
      </FilledActionButton>
    </>
  );

  return (
    <>
      <UIContainer>
        <BreadCrumbs actions={pageActions} />
      </UIContainer>
      <WithLoader loading={loading && !data.length}>
        <UIContainer sx={{ pb: 5 }}>
          <PageIntro
            title={pageTitle}
            description={pageDescription}
            footer={
              <VocabularyStatsGrid
                total={totalElements}
                learnedCount={aggregates.learned}
                activeCount={aggregates.active}
                posBreakdown={aggregates.breakdown}
              />
            }
          />
          <Box sx={{ mt: introCollapsed ? 2.5 : 0 }}>
          <VocabularyWordsView
            words={typedData}
            partOfSpeechOptions={partOfSpeechOptions}
            view={view}
            tableSettings={tableSettings}
            onSaveTableSettings={saveTableSettings}
            onResetTableSettings={resetTableSettings}
            onSetStatus={handleSetStatus}
            onDeleteOne={handleDeleteOne}
            onBulkSetStatus={handleBulkSetStatus}
            onBulkDelete={handleBulkDelete}
            undoMessage={undoMessage}
            onUndo={handleUndo}
            onUndoDismiss={() => setUndoMessage(null)}
            paginationFooter={paginationFooter}
          />
          </Box>
        </UIContainer>
      </WithLoader>

      <Dialog
        disableScrollLock
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{'Підтвердження видалення'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDeleteIds.length === 1
              ? 'Видалити це слово з вашого словника?'
              : `Видалити вибрані слова (${pendingDeleteIds.length}) з вашого словника?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {'Скасувати'}
          </Button>
          <Button color={'error'} variant={'contained'} onClick={confirmDelete}>
            {'Видалити'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VocabularyWordsPage;
